import React, { useState } from 'react';
import styles from './integrations-settings.module.css';
import hsFetch, { getCSRFToken } from 'js/sign-components/common/hs-fetch';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import {
  getBrandName,
  getSupportEmailAddress,
} from 'js/sign-components/common/brand';
import HfReactHelper from 'js/sign-components/common/hf-react-helper';

import { Split } from '@dropbox/dig-foundations';
import { Text } from '@dropbox/dig-components/typography';
import { Table } from '@dropbox/dig-components/table';
import { Button } from '@dropbox/dig-components/buttons';

type Integration = {
  name: string;
  isActive?: boolean;
  isEnabled: boolean;
  serviceTypeCode?: string;
  link?: string;
  buttonId: string;
};

type OnUpdateExternalServiceFn = (
  integration: Integration,
  setIsBusy: (value: boolean) => void,
) => void;

type IntegrationsSettingsProps = {
  integrations: Integration[];
};

type IntegrationsSettingsTableProps = {
  integrations: Integration[];
  onUpdateExternalService: OnUpdateExternalServiceFn;
};

type IntegrationTableRowProps = {
  integration: Integration;
  onUpdateExternalService: OnUpdateExternalServiceFn;
};

type IntegrationActionButtonProps = {
  isEnabled: boolean;
  isBusy: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isActive?: boolean;
  showActionButton: boolean;
  link?: string;
};

const ExternalServiceUpdateURL = '/account/updateExternalService';
const MyAccountIntegrationsHomeURL = '/home/myAccount?current_tab=integrations';

const messages = defineMessages({
  error: {
    id: '',
    description: 'Message shown to the user when an unknown error occurs',
    defaultMessage:
      'An error occurred, try again or contact support at {supportEmail}',
  },

  moreInfo: {
    id: '',
    description:
      'button text, integration page, short for more information about the integration',
    defaultMessage: 'More Info',
  },

  disabled: {
    id: '',
    description: 'Integration is disabled, button is disabled, can not be used',
    defaultMessage: 'Disabled',
  },

  activate: {
    id: '',
    description:
      'button text in setting page, when clicked activates third party integration',
    defaultMessage: 'Activate',
  },

  deactivate: {
    id: '',
    description:
      'button text in setting page, when clicked deactivates third party integration',
    defaultMessage: 'Deactivate',
  },

  dropboxDescription: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Forward signed emails and requested signature documents to Dropbox.{break} Allow importing of Dropbox files into {brandName}.',
  },

  dropboxDescriptionHF: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Forward faxes, signed emails, and requested signature documents to Dropbox.{break} Allow importing of Dropbox files into {brandName}.',
  },

  gmailDescription: {
    id: 'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Easily sign your attachments in Gmail and send them back immediately.',
  },

  gdocsTitle: {
    id: '',
    description: 'The title for the "Google Docs" integration.',
    defaultMessage: 'Google Docs',
  },

  gdocsDescription: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Easily sign documents and request signatures in Google Docs.',
  },

  gdriveDescriptionHF: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Forward faxes, signed emails, and requested signature documents to Google Drive.{break} Allow importing of Google Drive files into {brandName}.',
  },

  gdriveDescription: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Forward signed emails and requested signature documents to Google Drive.{break} Allow importing of Google Drive files into {brandName}.',
  },

  salesForceDescription: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Send and manage signature requests without ever needing to leave Salesforce.{break} Requires a {brandName} Standard plan or above.',
  },

  wordDescription: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration among of list of other integrations',
    defaultMessage:
      'Add your own legally-valid signature or request a signature directly from a Microsoft Word document.',
  },

  zapierDescription: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Join the no-code movement! Connect with dozens of popular services using {brandName} as either a "trigger" or an "action". For example, you can send signature requests from templates automatically and collect the information in a spreadsheet afterward.',
  },

  boxDescriptionHF: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Forward faxes, signed emails, and requested signature documents to Box.{break} Allow importing of Box files into {brandName}.',
  },

  boxDescription: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Forward signed emails and requested signature documents to Box.{break} Allow importing of Box files into {brandName}.',
  },

  oneDriveDescriptionHF: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Forward faxes, signed emails, and requested signature documents to OneDrive.{break} Allow importing of OneDrive files into {brandName}.',
  },

  oneDriveDescription: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Forward signed emails and requested signature documents to OneDrive.{break} Allow importing of OneDrive files into {brandName}.',
  },

  hubspotDescription: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Add {brandName} lifecycle events to the HubSpot Timeline as they take place.{break} Attach {brandName} properties to HubSpot Contacts for easy viewing.{break} Create signature requests from HubSpot based on templates and contact data.',
  },

  slackDescription: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Automatically receive updates on {brandName} documents as messages posted to your{break} Slack channels or private rooms.',
  },

  slackDescriptionActive: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'To change where updates are being posted,{break}deactivate the integration and activate once again.',
  },

  qqCatalystDescription: {
    id: '',
    description:
      'integration description, settings page, describes the features of a 3rd party integration  among of list of other integrations',
    defaultMessage:
      'Sync QQ Catalyst documents back to QQ Catalyst once they are signed by all parties.{break} Allow importing of QQ Catalyst files into {brandName}.',
  },
});

function lookupDescriptions(integration: Integration, isHelloFax: boolean) {
  const brandNameHS = getBrandName('S');
  const brandNameHF = getBrandName('F');

  const { name, isActive } = integration;

  const commonValues = {
    brandName: isHelloFax ? brandNameHF : brandNameHS,
    break: <br />,
  };

  const integrations = {
    Dropbox: (
      <FormattedMessage
        {...messages.dropboxDescription}
        values={commonValues}
      />
    ),
    Gmail: (
      <FormattedMessage {...messages.gmailDescription} values={commonValues} />
    ),
    'Google Docs': (
      <FormattedMessage {...messages.gdocsDescription} values={commonValues} />
    ),
    'Google Drive': (
      <FormattedMessage {...messages.gdriveDescription} values={commonValues} />
    ),
    [`${brandNameHS} for Salesforce`]: (
      <FormattedMessage
        {...messages.salesForceDescription}
        values={commonValues}
      />
    ),
    'Microsoft Word': (
      <FormattedMessage {...messages.wordDescription} values={commonValues} />
    ),
    Zapier: (
      <FormattedMessage {...messages.zapierDescription} values={commonValues} />
    ),
    Box: (
      <FormattedMessage {...messages.boxDescription} values={commonValues} />
    ),
    OneDrive: (
      <FormattedMessage
        {...messages.oneDriveDescription}
        values={commonValues}
      />
    ),
    HubSpot: (
      <FormattedMessage
        {...messages.hubspotDescription}
        values={commonValues}
      />
    ),
    Slack: (
      <React.Fragment>
        <FormattedMessage
          {...messages.slackDescription}
          values={{
            brandName: brandNameHS,
            break: <br />,
          }}
        />

        {isActive && (
          <FormattedMessage
            {...messages.slackDescriptionActive}
            values={{
              break: <br />,
            }}
          />
        )}
      </React.Fragment>
    ),
    'QQ Catalyst': (
      <FormattedMessage
        {...messages.qqCatalystDescription}
        values={commonValues}
      />
    ),
  };

  const integrationsHF = {
    Dropbox: (
      <FormattedMessage
        {...messages.dropboxDescriptionHF}
        values={commonValues}
      />
    ),
    'Google Drive': (
      <FormattedMessage
        {...messages.gdriveDescriptionHF}
        values={commonValues}
      />
    ),
    Box: (
      <FormattedMessage {...messages.boxDescriptionHF} values={commonValues} />
    ),
    OneDrive: (
      <FormattedMessage
        {...messages.oneDriveDescriptionHF}
        values={commonValues}
      />
    ),
  };

  return isHelloFax
    ? integrationsHF[name as keyof typeof integrationsHF] ?? null
    : integrations[name as keyof typeof integrations] ?? null;
}

function IntegrationActionButton({
  isEnabled,
  isBusy,
  isActive,
  onClick,
  showActionButton,
  link,
}: IntegrationActionButtonProps) {
  if (showActionButton) {
    const enabledMessage = isActive ? messages.deactivate : messages.activate;
    const message = isEnabled ? enabledMessage : messages.disabled;

    return (
      <Button
        data-testid="integration-action-button"
        className={styles.integrationButton}
        variant={isActive && isEnabled ? 'opacity' : 'primary'}
        type="button"
        target="_blank"
        rel="noopener noreferrer"
        disabled={!isEnabled}
        onClick={onClick}
        isLoading={isBusy}
      >
        <FormattedMessage {...message} />
      </Button>
    );
  } else {
    return (
      <Button
        data-testid="integration-action-button"
        className={styles.integrationButton}
        type="button"
        variant="borderless"
        href={link}
      >
        <FormattedMessage {...messages.moreInfo} />
      </Button>
    );
  }
}

function IntegrationTableRow({
  integration,
  onUpdateExternalService,
}: IntegrationTableRowProps) {
  const [isBusy, setIsBusy] = useState(false);

  const isHelloFax = HfReactHelper.isHelloFax();
  const { name, isActive, isEnabled, serviceTypeCode, link, buttonId } =
    integration;
  const description = lookupDescriptions(integration, isHelloFax);

  if (!description) {
    return null;
  }

  return (
    <Table.Row data-testid={`integration-${name}`}>
      <Table.Cell>
        <Split gap="Micro XSmall" direction="vertical">
          <Split.Item>
            <Text data-testid="integration-name" size={'large'}>
              {name}
            </Text>
          </Split.Item>

          <Split.Item>
            <Text color="subtle">{description}</Text>
          </Split.Item>
        </Split>
      </Table.Cell>
      <Table.Cell id={buttonId}>
        <IntegrationActionButton
          isEnabled={isEnabled}
          isBusy={isBusy}
          isActive={isActive}
          onClick={() => onUpdateExternalService(integration, setIsBusy)}
          showActionButton={
            !!serviceTypeCode || (name === 'Zapier' && !isEnabled)
          }
          link={link}
        />
      </Table.Cell>
    </Table.Row>
  );
}

export function IntegrationsSettingsTable({
  integrations,
  onUpdateExternalService,
}: IntegrationsSettingsTableProps) {
  return (
    <Table spacing="xlarge">
      <Table.Body>
        {integrations.map((integration) => {
          return (
            <IntegrationTableRow
              key={integration.name}
              integration={integration}
              onUpdateExternalService={onUpdateExternalService}
            />
          );
        })}
      </Table.Body>
    </Table>
  );
}

export default function IntegrationSettings({
  integrations,
}: IntegrationsSettingsProps) {
  const intl = useIntl();

  const onUpdateExternalService: OnUpdateExternalServiceFn = async (
    integration,
    setIsBusy,
  ) => {
    const { isActive, serviceTypeCode } = integration;

    const isHelloFax = HfReactHelper.isHelloFax();
    const siteCode = isHelloFax ? 'F' : 'S';

    const formData = new FormData();

    formData.append('service_type', serviceTypeCode ?? '');
    formData.append('is_deactivate', isActive ? '1' : '0');
    formData.append('csrf_token', getCSRFToken());

    setIsBusy(true);

    try {
      const response = await hsFetch(ExternalServiceUpdateURL, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.auth_url) {
        window.location.assign(data.auth_url.replace(/&amp;/g, '&')); // FIXME: Needed because it gets returned HTML-encoded
      } else {
        window.location.assign(MyAccountIntegrationsHomeURL);
      }
    } catch (e) {
      setIsBusy(false);
      // eslint-disable-next-line no-alert
      alert(
        intl.formatMessage(messages.error, {
          supportEmail: getSupportEmailAddress(siteCode),
        }),
      );
    }
  };

  return (
    <IntegrationsSettingsTable
      integrations={integrations}
      onUpdateExternalService={onUpdateExternalService}
    />
  );
}
